<template>
    <div class="document-ai-webview-content max-z-index" v-show="isShowDocumentAI">
        <!-- Show loader while the iframe is loading -->
        <div class="iframe-loading" v-loading="isLoading" v-if="isLoading"></div>
        <!-- Only render iframe when URL is available -->
        <iframe
            v-show="!!documentAIUrl"
            width="100%"
            @load="onIframeLoad"
            :src="documentAIUrl"
            frameborder="0"
            ref="documentAIIframe"
            id="documentAIIframe"
            class="document-ai-webview-iframe"
        ></iframe>
    </div>
</template>
<script>
import { WEBAPP_LAYOUT } from "../../../common/constants";
import { storeModules } from "../../../store";
import { AI_URI } from "../../../common/uri/property-uri.constants";
import { DOCUMENT_AI_EVENTS } from "../../../common/document-ai.constant";
import { postMessageForWebview, logWinAppMessage, logIframeMessageReceived } from "../../../utils/web-view";
import { getAuthConfig } from "../../../utils/auth-helper";
import { embeddedAppTokenBroker } from "../../../utils/embedded-app-token";
import { LanguageHelper } from "../../../utils/language-helper";

export default {
    name: 'DocumentAIWebview',
    data() {
        return {
            documentAIUrl: '',
            isShowDocumentAI: false,
            isLoading: true,
            language: 'english',
            propertyId: null,
        }
    },
    computed: {
        webappLayoutMode() {
            return this.$store.state[storeModules.Common].webappLayoutMode;
        },
        isWebview() {
            return this.webappLayoutMode === WEBAPP_LAYOUT.WEBVIEW;
        },
        expectedIframeOrigin() {
            try {
                const url = new URL(AI_URI.PDF_EXTRACTION);
                return url.origin;
            } catch {
                return '';
            }
        }
    },
    methods: {
        onIframeLoad() {
            this.isLoading = false;
        },
        async authenticateAndLoadDocumentAI() {
            try {
                if (!getAuthConfig().oauth2.accessToken) {
                    console.error("Authentication failed: Missing access token.");
                    this.$message.error('Authentication failed: Missing access token.');
                    this.isLoading = false;
                    return;
                }

                const propertyId = this.propertyId || '';
                const language = this.language || 'english';

                // WinApp relays only a single `save`: keep the PDF iframe single-PDF and without Minimize.
                this.documentAIUrl = `${AI_URI.PDF_EXTRACTION}?propertyId=${propertyId}&language=${language}&minimize=false&multi=false`;
            } catch (error) {
                console.error("Error authenticating and loading Document AI app:", error);
                this.$message.error('Error authenticating and loading Document AI app.');
                this.isLoading = false;
            }
        },
        handleMessage(event) {
            // Validate message origin (strict check â€” reject if origin doesn't match)
            if (event.origin !== this.expectedIframeOrigin) return;

            const iframe = this.$refs.documentAIIframe;
            if (!iframe?.contentWindow || event.source !== iframe.contentWindow) return;

            const { data } = event;
            if (!data || typeof data !== 'object') return;

            const messageType = data.TYPE || data.type;

            logIframeMessageReceived('DocumentAI', data, 'DocumentAI');

            switch (messageType) {
                case DOCUMENT_AI_EVENTS.CANCEL: {
                    postMessageForWebview("documentAICancel", null, 'DocumentAI');
                    break;
                }
                case DOCUMENT_AI_EVENTS.SAVE: {
                    const { documentType, fields, fieldConfidences, overallConfidence, document } = data.data || {};
                    postMessageForWebview("documentAISave", {
                        propertyId: this.propertyId,
                        documentType,
                        fields,
                        fieldConfidences,
                        overallConfidence,
                        document: document || null
                    }, 'DocumentAI');
                    break;
                }
                default:
                    break;
            }
        },
        async initData() {
            if (this.isWebview) {
                const vm = window;
                if (vm.chrome && vm.chrome.webview) {
                    vm.chrome.webview.addEventListener("message", async (event) => {
                        logWinAppMessage(event.data?.message, event.data?.data, 'DocumentAI');

                        if (event.data.message === "documentAIInfo") {
                            const { language, propertyId } = event.data.data;
                            this.language = language || 'english';
                            this.propertyId = propertyId;
                            await this.authenticateAndLoadDocumentAI();
                        }
                    });
                }
                postMessageForWebview("documentAICompleteLoading", null, 'DocumentAI');
            }
        }
    },
    async created() {
        this.isShowDocumentAI = true;
        this.handleMessage = this.handleMessage.bind(this);
        window.addEventListener("message", this.handleMessage);
        this.initData();
    },
    mounted() {
        this.unregisterAccessToken = embeddedAppTokenBroker.register({
            origin: this.expectedIframeOrigin,
            getContentWindow: () => this.$refs.documentAIIframe?.contentWindow || null,
            getLanguageId: () => LanguageHelper.convertLanguageInt(this.$i18n.locale),
        });
    },
    beforeDestroy() {
        window.removeEventListener("message", this.handleMessage);
        this.unregisterAccessToken?.();
    }
}
</script>
<style scoped>
.document-ai-webview-iframe {
    position: fixed;
    top: 0;
    height: 100%;
    width: 100%;
    left: 0;
    z-index: 9999;
}
</style>
